/**
 * Display labels for fund nature and category chips.
 *
 * Belongs here: text for backend nature/category enums on the fund header and plan cards.
 * Does not belong here: browse-tab category config or API mapping.
 */
import { toPascalCaseWithSpaces } from './utils'

/** Natures that must stay as sent. Everything else is pascal-cased. */
const FUND_NATURE_LABELS: Record<string, string> = {
  GIFT: 'GIFT'
}

/**
 * Label for the id after a known prefix (`GLOBAL_`, `GIFT_`).
 * One entry covers both prefixes, so `GIFT_US_NASDAQ_100` and `GLOBAL_US_NASDAQ_100` share a label.
 */
const CATEGORY_LABEL_BY_SUFFIX: Record<string, string> = {
  EMERGING_MARKETS: 'Emerging Markets',
  DIVERSIFIED_ALL_COUNTRIES: 'Diversified All Countries',
  EUROPE: 'Europe',
  OTHERS_ACTIVE: 'Others Active',
  INNOVATION_AND_DISRUPTION: 'Innovation & Disruption',
  OTHERS_PASSIVE: 'Others Passive',
  DEVELOPED_MARKETS: 'Developed Markets',
  US_NASDAQ_100: 'US NASDAQ 100',
  US_OTHERS_PASSIVE: 'US Others Passive',
  US_DIVERSIFIED_EQUITY: 'US Diversified Equity',
  OTHER: 'Other'
}

const CATEGORY_ID_PREFIXES = ['GLOBAL_', 'GIFT_']

/** Fund nature chip. `GIFT` stays `GIFT`; other enums are pascal-cased. */
export function formatFundNatureLabel(nature: string | undefined | null): string {
  const raw = nature ?? '-'
  return FUND_NATURE_LABELS[raw] ?? toPascalCaseWithSpaces(raw)
}

/** Fund category chip. Prefixed ids use the suffix map; unknown suffixes are pascal-cased without the prefix. */
export function formatFundCategoryLabel(category: string | undefined | null): string {
  const raw = category ?? '-'
  if (raw === 'COMMODITY_GOLD_OTHERS') return 'Gold Parking Fund'
  if (raw === 'GLOBAL_OTHER') return 'Global Parking Fund'


  const prefixedLabel = labelForPrefixedCategory(raw)
  if (prefixedLabel != null) return prefixedLabel

  return toPascalCaseWithSpaces(raw)
    .replace(/\bcommodity\b/gi, '')
    .replace(/fund\s+of\s+funds/gi, '')
    .trim()
    .replace(/\s+/g, ' ')
}

function labelForPrefixedCategory(categoryId: string): string | null {
  for (const prefix of CATEGORY_ID_PREFIXES) {
    if (!categoryId.startsWith(prefix)) continue
    const suffix = categoryId.slice(prefix.length)
    return CATEGORY_LABEL_BY_SUFFIX[suffix] ?? toPascalCaseWithSpaces(suffix)
  }
  return null
}
